import { useEffect, useState } from "react";
import { Link, Navigate, NavLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast, Toaster } from "sonner";
import { LogOut } from "lucide-react";

export function AdminShell({ children, active }) {
  const { user, logout } = useAuth();
  if (user === null) return null;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;
  return (
    <div style={{ minHeight: "100vh", background: "var(--ivory)" }}>
      <Toaster position="top-center" />
      <header style={{ background: "var(--black)", color: "var(--ivory)", padding: "20px 32px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 32 }}>
          <Link to="/admin/applications" className="brand-mark" style={{ fontSize: "1.2rem" }}>SAVIAR <span style={{ color: "var(--gold)", fontSize: ".65rem", letterSpacing: ".3em" }}>ADMIN</span></Link>
          <nav style={{ display: "flex", gap: 20 }}>
            <NavLink to="/admin/applications" style={({ isActive }) => ({ color: isActive ? "var(--gold-light)" : "rgba(250,246,238,.6)", fontSize: ".72rem", letterSpacing: ".2em", textTransform: "uppercase", textDecoration: "none" })} data-testid="admin-nav-apps">Applications</NavLink>
            <NavLink to="/admin/clients" style={({ isActive }) => ({ color: isActive ? "var(--gold-light)" : "rgba(250,246,238,.6)", fontSize: ".72rem", letterSpacing: ".2em", textTransform: "uppercase", textDecoration: "none" })} data-testid="admin-nav-clients">Clients</NavLink>
            <NavLink to="/admin/blog" style={({ isActive }) => ({ color: isActive ? "var(--gold-light)" : "rgba(250,246,238,.6)", fontSize: ".72rem", letterSpacing: ".2em", textTransform: "uppercase", textDecoration: "none" })} data-testid="admin-nav-blog">Blog</NavLink>
          </nav>
        </div>
        <button onClick={logout} style={{ background: "transparent", border: "none", color: "var(--gold-light)", display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: ".7rem", letterSpacing: ".2em", textTransform: "uppercase" }} data-testid="admin-logout">
          <LogOut size={14} /> Sign out
        </button>
      </header>
      <main className="container-x" style={{ padding: "40px 24px" }}>
        {children}
      </main>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { bg: "rgba(201,185,154,.3)", fg: "#7A5F2A" },
    approved: { bg: "rgba(76,140,89,.15)", fg: "#3D7A4A" },
    rejected: { bg: "rgba(196,115,122,.2)", fg: "#8B2A3A" },
  };
  const c = map[status] || map.pending;
  return <span style={{ background: c.bg, color: c.fg, padding: "4px 10px", fontSize: ".64rem", letterSpacing: ".2em", textTransform: "uppercase" }}>{status}</span>;
}

export function AdminApplications() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/applications"); setRows(data); }
    catch { toast.error("Failed to load applications"); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/applications/${id}`, { status }); toast.success(`Marked ${status}.`); load(); }
    catch { toast.error("Update failed"); }
  };

  const filtered = filter === "all" ? rows : rows.filter((r) => r.status === filter);

  return (
    <AdminShell active="apps">
      <p className="eyebrow" style={{ marginBottom: 8 }}>Admin</p>
      <h1 className="display" style={{ fontSize: "2rem", color: "var(--charcoal)", marginBottom: 24 }}>Applications</h1>
      <div style={{ display: "flex", gap: 4, marginBottom: 20 }} data-testid="app-filter-tabs">
        {["all", "pending", "approved", "rejected"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} className="btn"
            style={{ padding: "10px 18px", background: filter === f ? "var(--charcoal)" : "transparent", color: filter === f ? "var(--ivory)" : "var(--charcoal)", border: "1px solid var(--charcoal)" }}
            data-testid={`filter-${f}`}>
            {f} {f === "all" ? `(${rows.length})` : `(${rows.filter(r => r.status === f).length})`}
          </button>
        ))}
      </div>
      <div className="portal-card" style={{ padding: 0 }} data-testid="applications-table">
        {filtered.length === 0 ? (
          <p style={{ padding: 40, textAlign: "center", color: "var(--warm-gray)" }}>No applications.</p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(184,150,90,.2)", textAlign: "left" }}>
                {["Name", "Email", "City", "Month", "Status", "Date", ""].map((h) => (
                  <th key={h} style={{ padding: 16, fontSize: ".68rem", letterSpacing: ".2em", textTransform: "uppercase", color: "var(--warm-gray)", fontWeight: 500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <>
                  <tr key={r.id} onClick={() => setExpanded(expanded === r.id ? null : r.id)} style={{ borderBottom: "1px solid rgba(184,150,90,.1)", cursor: "pointer" }} data-testid={`app-row-${r.id}`}>
                    <td style={{ padding: 16, fontFamily: "var(--serif)", fontSize: "1.05rem" }}>{r.full_name}</td>
                    <td style={{ padding: 16, fontSize: ".88rem" }}>{r.email}</td>
                    <td style={{ padding: 16, fontSize: ".88rem" }}>{r.city_of_origin || "—"}</td>
                    <td style={{ padding: 16, fontSize: ".88rem" }}>{r.preferred_start_month || "—"}</td>
                    <td style={{ padding: 16 }}><StatusBadge status={r.status || "pending"} /></td>
                    <td style={{ padding: 16, fontSize: ".82rem", color: "var(--warm-gray)" }}>{new Date(r.created_at).toLocaleDateString()}</td>
                    <td style={{ padding: 16 }} onClick={(e) => e.stopPropagation()}>
                      <select onChange={(e) => setStatus(r.id, e.target.value)} value={r.status || "pending"}
                        style={{ padding: "6px 10px", background: "transparent", border: "1px solid rgba(184,150,90,.4)", fontSize: ".78rem" }}
                        data-testid={`status-select-${r.id}`}>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </td>
                  </tr>
                  {expanded === r.id && (
                    <tr>
                      <td colSpan={7} style={{ background: "rgba(184,150,90,.06)", padding: 24, fontSize: ".88rem", lineHeight: 1.7 }}>
                        <p><strong>Phone:</strong> {r.phone || "—"} · <strong>DOB:</strong> {r.date_of_birth || "—"}</p>
                        <p><strong>What brought her:</strong> {r.what_brought_you || r.story || "—"}</p>
                        <p><strong>Soft life vision:</strong> {r.soft_life_vision || "—"}</p>
                        <p><strong>Prior program experience:</strong> {r.prior_program_experience || "—"}</p>
                        <hr style={{ border: 0, borderTop: "1px solid rgba(184,150,90,.2)", margin: "12px 0" }} />
                        <p><strong>Health history:</strong> {r.health_history || "—"}</p>
                        <p><strong>Medications:</strong> {r.medications || "—"}</p>
                        <p><strong>Dietary:</strong> {r.dietary_restrictions || "—"}</p>
                        <p><strong>Mental health notes:</strong> {r.mental_health_notes || "—"}</p>
                        <hr style={{ border: 0, borderTop: "1px solid rgba(184,150,90,.2)", margin: "12px 0" }} />
                        <p><strong>Emergency:</strong> {r.emergency_name || "—"} ({r.emergency_relation || "—"}) · {r.emergency_phone || "—"}</p>
                        <hr style={{ border: 0, borderTop: "1px solid rgba(184,150,90,.2)", margin: "12px 0" }} />
                        <p><strong>Acknowledgments:</strong>{" "}
                          Waiver {r.ack_liability_waiver ? "✓" : "✗"} ·
                          Risk {r.ack_assumption_of_risk ? "✓" : "✗"} ·
                          Health {r.ack_health_disclosure ? "✓" : "✗"} ·
                          Participation {r.ack_participation_agreement ? "✓" : "✗"} ·
                          Privacy {r.ack_privacy_policy ? "✓" : "✗"} ·
                          T&C {r.ack_terms ? "✓" : "✗"} ·
                          Media {r.ack_media_release ? "✓" : "—"}
                        </p>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminShell>
  );
}

export function AdminClients() {
  const [rows, setRows] = useState([]);
  useEffect(() => { (async () => { try { const { data } = await api.get("/admin/clients"); setRows(data); } catch { toast.error("Failed to load clients"); } })(); }, []);
  return (
    <AdminShell active="clients">
      <p className="eyebrow" style={{ marginBottom: 8 }}>Admin</p>
      <h1 className="display" style={{ fontSize: "2rem", color: "var(--charcoal)", marginBottom: 24 }}>Clients</h1>
      <div className="portal-card" style={{ padding: 0 }} data-testid="clients-table">
        {rows.length === 0 ? (
          <p style={{ padding: 40, textAlign: "center", color: "var(--warm-gray)" }}>No clients yet.</p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(184,150,90,.2)", textAlign: "left" }}>
                {["Name", "Email", "Day", "Start Date", "Joined"].map((h) => (
                  <th key={h} style={{ padding: 16, fontSize: ".68rem", letterSpacing: ".2em", textTransform: "uppercase", color: "var(--warm-gray)", fontWeight: 500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderBottom: "1px solid rgba(184,150,90,.1)" }} data-testid={`client-row-${r.id}`}>
                  <td style={{ padding: 16, fontFamily: "var(--serif)", fontSize: "1.05rem" }}>{r.name}</td>
                  <td style={{ padding: 16, fontSize: ".88rem" }}>{r.email}</td>
                  <td style={{ padding: 16, fontSize: ".88rem" }}>Day {r.day} / 21</td>
                  <td style={{ padding: 16, fontSize: ".88rem" }}>{r.start_date || "—"}</td>
                  <td style={{ padding: 16, fontSize: ".82rem", color: "var(--warm-gray)" }}>{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminShell>
  );
}
